import {
  DoorPositionMappinDto,
  EditHubDto,
  EditLockersHubSetupDto,
  SetupExternalLockersHubDto,
  SetupLockersHubDto,
  StraightLineTowerLayoutConfig,
  TowerModelDto
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { HubLayoutForm, HubLayoutTowerRow } from './form';
import { getEnvironment } from '../../../../core/env/environment';
import { getDefaultKioskBackgroundUrl } from '../shared/default-kiosk-backgrounds';
import { HubDetails } from '../../../lockers/models/hub-details.model';

interface KioskBrandingConfig {
  background_home: string;
  dark_theme: boolean;
  logo: { type: string; home_screen_height_px: number; src: string };
  powered_by_theme: string;
  background_session: string;
}

interface KioskHomeUiConfig {
  button_rows: { action: string; label: { default: string }; style: string }[][];
}

interface ClientKioskConfig {
  // kiosk_identity is built on the API each time the config is served
  mode: { type: string };
  branding: KioskBrandingConfig;
  home_ui: KioskHomeUiConfig;
}

interface KioskConfig {
  home_ui: KioskHomeUiConfig;
  hardware_configuration: { locker_type: string; scanner_mode: string };
  courier_login: { allow_signup: boolean };
  hub_configuration: { type: string };
  transaction_parameters: {
    inbound_parcel: {
      recipient_search: {
        name: { enabled: boolean; min_length: number };
        mobile: { enabled: boolean };
      };
    };
  };
  mode: { type: string };
  transactions: { type: string }[];
  branding: KioskBrandingConfig;
}

export class HubLayoutFormOutput {
  name?: string;
  manufacturer?: string;
  iotHubID?: string;
  towers: HubLayoutTowerRow[];
  frontName?: string;
  backName?: string;

  constructor(form: HubLayoutForm) {
    this.manufacturer = form.manufacturer;
    this.iotHubID = form.iotHubID;
    this.towers = form.towers ?? [];
    this.frontName = form.frontName;
    this.backName = form.backName;
  }

  static FromHubDetails(hubDetails: HubDetails): HubLayoutForm {
    const sortedMappings = [...(hubDetails.doorPositionMappings ?? [])].sort((a, b) => a.position - b.position);

    return {
      name: hubDetails.name,
      iotHubID: hubDetails.kiosk?.iotHubID,
      towers: (hubDetails.towers ?? []).map((tower, index) => ({
        model: tower.modelName,
        inventoryID: tower.inventoryID ?? '',
        _key: `server-${index}`
      })),
      frontName: sortedMappings[0]?.description,
      backName: sortedMappings[1]?.description
    };
  }

  static ToSetupDto(output: HubLayoutFormOutput, hubID: string, towerModels: TowerModelDto[]): SetupLockersHubDto {
    const straightLineTowerLayout: StraightLineTowerLayoutConfig[] = output.towers
      .filter((t) => t.model && t.inventoryID)
      .map(({ model, inventoryID }) => ({ model, inventoryID }));

    const needsClientKiosk = output.towers.some((t) => {
      const model = towerModels.find((m) => m.name === t.model);
      return model?.hasClientKiosk === true;
    });

    const env = getEnvironment();
    const blobHost = `https://gflockercloudbroker${env.type}.blob.core.windows.net`;
    const kioskBrandingConfig: KioskBrandingConfig = {
      background_home: getDefaultKioskBackgroundUrl('light'),
      dark_theme: false,
      logo: {
        type: 'branded',
        home_screen_height_px: 407,
        src: `${blobHost}/assets/icon-groundfloor.png`
      },
      powered_by_theme: 'mono',
      background_session: getDefaultKioskBackgroundUrl('light')
    };
    const kioskHomeUiConfig: KioskHomeUiConfig = {
      button_rows: [
        [
          { action: 'internal_session', label: { default: 'Customers' }, style: 'primary' },
          { action: 'external_session', label: { default: 'Couriers' }, style: 'primary' }
        ]
      ]
    };
    const kioskConfig: KioskConfig = {
      home_ui: kioskHomeUiConfig,
      hardware_configuration: {
        locker_type: (output.manufacturer?.toLowerCase() ?? 'hzdc').toLowerCase(),
        scanner_mode: 'cdc'
      },
      courier_login: { allow_signup: true },
      hub_configuration: { type: 'locker' },
      transaction_parameters: {
        inbound_parcel: {
          recipient_search: {
            name: { enabled: true, min_length: 3 },
            mobile: { enabled: true }
          }
        }
      },
      mode: { type: 'in_service' },
      transactions: [{ type: 'inbound_parcel_delivery' }, { type: 'inbound_parcel_collection' }],
      branding: kioskBrandingConfig
    };

    const clientKioskConfig: ClientKioskConfig = {
      mode: { type: 'in_service' },
      branding: kioskBrandingConfig,
      home_ui: kioskHomeUiConfig
    };

    return {
      hubID,
      iotHubID: output.iotHubID,
      kioskConfig: JSON.stringify(kioskConfig) as unknown as string,
      ...(needsClientKiosk && { clientKioskConfig: JSON.stringify(clientKioskConfig) as unknown as string }),
      straightLineTowerLayout
    };
  }

  static ToSetupExternalDto(output: HubLayoutFormOutput, hubID: string): SetupExternalLockersHubDto {
    return {
      hubID,
      straightLineTowerLayout: output.towers
        .filter((t) => t.model && t.inventoryID)
        .map(({ model, inventoryID }) => ({ model, inventoryID }))
    };
  }

  static ToEditDto(output: HubLayoutFormOutput): EditLockersHubSetupDto {
    return {
      straightLineTowerLayout: output.towers
        .filter((t) => t.model && t.inventoryID)
        .map(({ model, inventoryID }) => ({ model, inventoryID })),
      deleteExcludedTowers: true
    };
  }

  static ToDoorPositionMappings(output: HubLayoutFormOutput): DoorPositionMappinDto[] | undefined {
    if (!output.frontName && !output.backName) return undefined;
    const mappings: DoorPositionMappinDto[] = [];
    if (output.frontName) mappings.push({ position: 0, description: output.frontName });
    // Each tower is currently only 50cm deep. This may need to change in future.
    if (output.backName) mappings.push({ position: 50, description: output.backName });
    return mappings;
  }

  static ToEditHubDto(output: HubLayoutFormOutput): EditHubDto {
    const doorPositionMappings = HubLayoutFormOutput.ToDoorPositionMappings(output);
    return {
      ...(doorPositionMappings && { doorPositionMappings })
    };
  }
}
